import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  APP_CODE,
  getFirstReadableResourcePath,
  getResourceConfig,
  hasSubscriptionPermission,
  normalizeSubscriptionPermissions,
  RESOURCE_KEYS,
  RESOURCES,
} from './constants';
import { selectSubscriptionPermissions, selectUserLoading } from '../api/user/userSlice';

export function usePermissions() {
  const subscriptionPermissions = useSelector(selectSubscriptionPermissions);
  const userLoading = useSelector(selectUserLoading);

  return useMemo(() => {
    const normalizedPermissions = normalizeSubscriptionPermissions(subscriptionPermissions);

    const hasPermission = (code) => hasSubscriptionPermission(subscriptionPermissions, code);

    const canRead = (resourceKey) => {
      const config = getResourceConfig(resourceKey);

      return config ? hasPermission(config.read) : false;
    };

    const canCreate = (resourceKey) => {
      const config = getResourceConfig(resourceKey);

      return config ? hasPermission(config.create) : false;
    };

    const canUpdate = (resourceKey) => {
      const config = getResourceConfig(resourceKey);

      return config ? hasPermission(config.update) : false;
    };

    const hasAnySubscriptionPermission = () => subscriptionPermissions.length > 0;

    const getReadableResources = () =>
      RESOURCE_KEYS.filter((key) => canRead(key)).map((key) => ({
        key,
        ...RESOURCES[key],
      }));

    const getDefaultPath = () => getFirstReadableResourcePath(subscriptionPermissions);

    return {
      subscriptionPermissions,
      normalizedPermissions,
      userLoading,
      hasPermission,
      canRead,
      canCreate,
      canUpdate,
      hasAnySubscriptionPermission,
      getReadableResources,
      getDefaultPath,
      appCode: APP_CODE,
    };
  }, [subscriptionPermissions, userLoading]);
}
